import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { Tooltip, PieChart, Pie, Cell } from 'recharts';
import humps from 'humps';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { setDashboardData, getChartData, fetchEmployeeCategories } from 'src/reducers/dashboard';
import ListGroup from 'jbc-front/components/ListGroup';
import { openNewTab } from 'src/utils/Http';
import { EMPLOYEES_CATEGORIES } from 'src/constants/Options';
import Loading from 'src/components/Loading';
import { generateChartColor, sortUrl, employeeCategoryLinkURL } from 'src/utils/Dashboard';

import { ChartTable, sumNumber } from './ChartTable';
import ChartTooltips from './ChartTooltips';
import styles from './EmployeeCategory.scss';

export const getOptions = t =>
  EMPLOYEES_CATEGORIES.map(({ label, value }) => ({
    label: t(`features.Dashbord.components.EmployeeCategory.${label}`),
    value
  }));

const EmployeeCategory = ({ employeeCategories, setChart, chartData, dispatch }) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState('group');
  const [disabled, setDisabled] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setChart('employeeCategory', generateChartColor(employeeCategories));
  }, []);

  const handlePieClick = item => {
    const defaultUrl = '/employees?enrollment_status=2';
    const selectedCategory = humps.camelize(selected);
    const linkUrl = employeeCategoryLinkURL(item.id);
    const hasCategoryId = item.id > 0;
    const urlToOpen = hasCategoryId ? linkUrl[selectedCategory] : sortUrl[selectedCategory];
    openNewTab(`${defaultUrl}${urlToOpen}`);
  };

  const handleChangeCategory = async ({ value }) => {
    setIsLoading(true);
    setSelected(value);
    await dispatch(fetchEmployeeCategories(value));
    setDisabled([]);
    setIsLoading(false);
  };

  return (
    <div className="u-mt30">
      <ListGroup>
        <ListGroup.Title>{t(`features.Dashbord.components.EmployeeCategory.employee_category`)}</ListGroup.Title>
        <ListGroup.Item className={styles.list}>
          <Select
            wrapperStyle={{ width: '100%' }}
            options={getOptions(t)}
            value={selected}
            onChange={handleChangeCategory}
            searchable={false}
            clearable={false}
          />
          {isLoading ? (
            <Loading />
          ) : (
            <>
              <div className="d-flex justify-content-center">
                <PieChart height={260} width={240} cursor="pointer">
                  <Pie
                    data={chartData.map(data => ({
                      ...data,
                      number: disabled.includes(data.id.toString()) ? 0 : data.number
                    }))}
                    dataKey="number"
                    nameKey="name"
                    onClick={handlePieClick}
                  >
                    {chartData.map((entry, index) => <Cell key={`cell-${index + 1}`} fill={entry.color} />)}
                  </Pie>
                  <Tooltip content={<ChartTooltips />} wrapperStyle={{ zIndex: 20 }} />
                </PieChart>
                <div id="sumLabel" className={styles.sumLabel}>
                  {sumNumber(chartData, disabled)}
                  {t('features.Dashbord.components.EmployeeCategory.value_unit')}
                </div>
              </div>
              <div style={{ height: '130px' }}>
                <ChartTable
                  chartData={chartData}
                  nameTableLabel={t(`features.Dashbord.components.EmployeeCategory.${humps.decamelize(selected)}`)}
                  valueTableLabel={t(`features.Dashbord.components.EmployeeCategory.value_table_label`)}
                  disabled={disabled}
                  setdisabled={setDisabled}
                  maxHeight={'127px'}
                />
              </div>
            </>
          )}
        </ListGroup.Item>
      </ListGroup>
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  setChart: (key, object) => dispatch(setDashboardData(key, object)),
  dispatch
});

const mapStateToProps = state => ({
  chartData: getChartData(state, 'employeeCategory')
});

Tooltip.propTypes = {
  allowEscapeViewBow: PropTypes.shape({
    x: PropTypes.bool,
    y: PropTypes.bool
  })
};

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeCategory);
